import { useQuery } from "@tanstack/react-query";

import api from "@/api/api";

const REFETCH_INTERVAL_MS = 60_000;
const TIMEOUT_MS = 5_000;

/**
 * Detects whether the API host answers at all.
 *
 * server/app.ts calls app.listen() inside the mongoose connect callback, so a dead database means
 * the server never starts listening and every request fails at the network layer. A /health
 * endpoint would therefore be unreachable during exactly the outage it was meant to report.
 *
 * The request is a plain CORS request rather than no-cors. no-cors looks like the safer choice — an
 * opaque response that merely resolves would prove the host answered — but the API sends helmet's
 * default Cross-Origin-Resource-Policy: same-origin, and that header makes the browser block every
 * cross-origin no-cors response. The ping then failed permanently and reported a perfectly healthy
 * server as down. cors() runs ahead of every route and error handler, so any answer carries
 * Access-Control-Allow-Origin and resolves here — a 404 counts as "up", which is what we want.
 */
export async function pingBackend(): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    await fetch(api.defaults.baseURL ?? "", { method: "GET", cache: "no-store", signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export default function useBackendHealth(): boolean {
  const { data } = useQuery({
    queryKey: ["backend-health"],
    queryFn: pingBackend,
    refetchInterval: REFETCH_INTERVAL_MS,
    staleTime: 30_000,
    retry: 1,
  });

  // Treat "not answered yet" as up, so the banner never flashes during a normal page load.
  return data ?? true;
}
